<template>
  <v-overlay
    v-if="isFetchingCurrentConversation || isFetchingEntity"
    z-index="10"
  >
    <v-progress-circular indeterminate size="64" />
  </v-overlay>
  <div class="conversation-details" v-else-if="currentConversation">
    <AppLayout :title="title" centered :parent="parent" isDetails>
      <template v-slot:body>
        <div class="container col-md-8 col-sm-12">
          <RelatedToLink
            v-if="currentConversation.relatedToType"
            :entityType="currentConversation.relatedToType"
            :text="linkText"
            :linkPath="linkPath"
          />
          <ConversationMessageItem
            v-for="(message, index) in currentConversation.messages"
            :message="message"
            :isEmail="isEmail"
            :key="index"
          />
          <Permissioned
            vertical
            section="conversations"
            :id="currentConversationId"
          >
            <div class="new-message" permission="new-message">
              <div class="d-flex mt-2 align-center justify-space-between">
                <h3 class="mb-2">
                  Reply
                </h3>
                <Select
                  v-if="isEmail"
                  v-model="selectedTemplate"
                  :items="messageTemplates"
                  @change="onMessageTemplateChange"
                  item-text="subject"
                  :loading="isFetchingMessageTemplates"
                  :disabled="isFetchingMessageTemplates"
                  placeholder="Select Template"
                  autocomplete
                  label=""
                  fullWidth
                  dense
                />
              </div>
              <ValidationObserver ref="message">
                <div v-if="isEmail">
                  <quill-editor
                    v-if="isEmail"
                    ref="quillEditor"
                    v-model="body"
                    :options="editorOptions"
                    @ready="adjustVariablesTextContent()"
                  />
                  <div v-if="showValidationErrors & !body" class="text-error">
                    This field is required
                  </div>
                </div>
                <ValidationProvider
                  v-else
                  v-slot="{ errors }"
                  rules="required"
                  class="full-width d-flex"
                >
                  <v-textarea
                    :error-messages="getErrors(errors)"
                    height="200"
                    rounded
                    v-model="body"
                  />
                </ValidationProvider>
                <div
                  class="d-flex justify-space-between align-center flex-wrap"
                  :class="{ 'justify-end': !isEmail }"
                >
                  <FileUpload
                    v-if="isEmail"
                    :uploadingUrl="getAddFileUrl()"
                    class="mt-5"
                    @unselectFile="unselectFile"
                    @selectFile="selectFile"
                    ref="fileUpload"
                    :files="files"
                    disableAutoProceed
                    customActions
                    isMultiple
                  />
                  <PrimaryButton
                    @click.native="addMessage"
                    :loading="isLoading"
                    :class="{
                      'full-width': isMobile
                    }"
                  >
                    Send {{ isEmail ? "Email" : "Text" }}
                  </PrimaryButton>
                </div>
              </ValidationObserver>
            </div>
          </Permissioned>
        </div>
        <BaseFooter>
          <Permissioned
            :vertical="isMobile"
            section="conversations"
            :id="currentConversationId"
            class="actions-bar close-conversation-action"
          >
            <SecondaryButton
              permission="close"
              @click.native="closeConversation"
              textColor="#0965dc"
              :loading="isEditing"
            >
              <inline-svg
                :src="require('@/assets/svg/check_white.svg')"
                width="20"
                height="20"
              />
              Close Conversation
            </SecondaryButton>
          </Permissioned>
        </BaseFooter>
      </template>
    </AppLayout>
  </div>
</template>

<script>
import AppLayout from "@/components/common/base-layouts/AppLayout";
import { first, size, remove, map, forEach, get } from "lodash";
import { copyState } from "@/utils/helpers";
import { createNamespacedHelpers } from "vuex";
import { REFERENCES_NAMESPACE } from "@/store/modules/references";
import { FETCH_CURRENT_REFERENCE } from "@/store/modules/references/actions";
import { OFFERS_NAMESPACE } from "@/store/modules/offers";
import { FETCH_CURRENT_OFFER } from "@/store/modules/offers/actions";
import { CONVERSATIONS_NAMESPACE } from "@/store/modules/conversations";
import {
  FETCH_CURRENT_CONVERSATION,
  FETCH_MESSAGE_TEMPLATES,
  EDIT_CONVERSATION,
  CREATE_CONVERSATION_MESSAGE
} from "@/store/modules/conversations/actions";
import {
  BOOKINGS_NAMESPACE,
  FETCH_CURRENT_BOOKING
} from "@/store/modules/bookings/actions";
import {
  GET_CURRENT_BOOKING,
  IS_LOADING_BOOKING
} from "@/store/modules/bookings/getters";
import { getFilesFromUrls } from "@/utils/files";
import Permissioned from "@/components/common/Permissioned";
import BaseFooter from "@/components/common/BaseFooter";
import SecondaryButton from "@/components/common/Button/SecondaryButton";
import Select from "@/components/common/Select";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { DEFAULT_TOOLBAR_OPTIONS } from "@/plugins/v-quill-editor";
import { convertHtmlToText } from "@/views/conversations/modules/utils";
import ConversationMessageItem from "@/views/conversations/ConversationDetails/components/ConversationMessageItem";
import RelatedToLink from "@/views/conversations/ConversationDetails/components/RelatedToLink";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import FileUpload from "@/components/common/FileUpload";
import {
  BASIC_VARIABLES_TO_INSERT,
  insertVariable,
  getConvertedEmailBody,
  adjustVariablesTextContent,
  getVariablesToInsertByEntityType
} from "@/utils/conversations";
import APIService from "@/services/APIService";
import { getRelatedToDescriptionFullDescription } from "@/utils/conversations";
import InlineSvg from "vue-inline-svg";
import { convertBlobFileToBase64 } from "@/utils/files";

const {
  mapActions: mapConversationActions,
  mapState: mapConversationState
} = createNamespacedHelpers(CONVERSATIONS_NAMESPACE);

const {
  mapActions: mapOfferActions,
  mapState: mapOfferState
} = createNamespacedHelpers(OFFERS_NAMESPACE);

const {
  mapActions: mapReferenceActions,
  mapState: mapReferenceState
} = createNamespacedHelpers(REFERENCES_NAMESPACE);

const {
  mapActions: mapBookingActions,
  mapGetters: mapBookingGetters
} = createNamespacedHelpers(BOOKINGS_NAMESPACE);

export default {
  name: "ConversationDetails",
  props: {
    isBookingConversation: Boolean
  },
  components: {
    AppLayout,
    BaseFooter,
    Permissioned,
    SecondaryButton,
    Select,
    ConversationMessageItem,
    ValidationObserver,
    ValidationProvider,
    PrimaryButton,
    FileUpload,
    RelatedToLink,
    InlineSvg
  },
  data() {
    return {
      selectedTemplate: null,
      body: "",
      isEditing: false,
      isLoading: false,
      files: [],
      showValidationErrors: false,
      uppyInstance: null,
      attachments: []
    };
  },
  async created() {
    await this.fetchConversation(this.currentConversationId);
    if (!this.currentConversation) {
      return this.$router.push("/pageNotFound");
    }
    await this.fetchEntity();
    this.adjustMessageTemplates();
    document.title = this.pageTitle;
  },
  computed: {
    editorOptions() {
      return {
        modules: {
          toolbar: {
            container: [
              ...DEFAULT_TOOLBAR_OPTIONS,
              [
                {
                  "insert-variable": [
                    ...BASIC_VARIABLES_TO_INSERT,
                    ...getVariablesToInsertByEntityType(
                      this.currentConversation.relatedToType
                    )
                  ]
                }
              ]
            ],
            handlers: {
              "insert-variable": val =>
                insertVariable(val, this.$refs.quillEditor.quill)
            }
          }
        }
      };
    },
    linkText() {
      return getRelatedToDescriptionFullDescription(this.currentConversation);
    },
    linkPath() {
      const { relatedToType, relatedToId } = this.currentConversation;
      if (relatedToType === "booking") {
        return `/jobs/${relatedToId}`;
      }
      const offerId =
        relatedToType === "offer"
          ? relatedToId
          : get(this.currentReference, "offer.id");
      return `/offers/${offerId}`;
    },
    isFetchingEntity() {
      return (
        this.isFetchingCurrentReference ||
        this.isFetchingCurrentOffer ||
        this.isFetchingCurrentBooking
      );
    },
    currentConversationId() {
      return this.$route.params.conversationId;
    },
    ...mapBookingGetters({
      currentBooking: GET_CURRENT_BOOKING,
      isFetchingCurrentBooking: IS_LOADING_BOOKING
    }),
    ...mapConversationState(
      copyState([
        "currentConversation",
        "isFetchingCurrentConversation",
        "isFetchingMessageTemplates",
        "messageTemplates"
      ])
    ),
    ...mapReferenceState(
      copyState(["currentReference", "isFetchingCurrentReference"])
    ),
    ...mapOfferState(copyState(["currentOffer", "isFetchingCurrentOffer"])),
    isEmail() {
      return this.currentConversation.conversationFormat === "email";
    },
    title() {
      return this.isEmail
        ? this.currentConversation.subject
        : first(this.currentConversation.messages).body;
    },
    pageTitle() {
      return `${this.title} - Conversations - Track`;
    },
    parent() {
      return this.isBookingConversation && this.currentBooking
        ? `/jobs/${this.currentBooking.id}/conversations`
        : "/conversations";
    }
  },
  methods: {
    ...mapConversationActions({
      fetchConversation: FETCH_CURRENT_CONVERSATION,
      fetchMessageTemplates: FETCH_MESSAGE_TEMPLATES,
      editConversation: EDIT_CONVERSATION,
      addConversationMessage: CREATE_CONVERSATION_MESSAGE
    }),
    ...mapReferenceActions({
      fetchCurrentReference: FETCH_CURRENT_REFERENCE
    }),
    ...mapBookingActions({
      fetchCurrentBooking: FETCH_CURRENT_BOOKING
    }),
    ...mapOfferActions({
      fetchCurrentOffer: FETCH_CURRENT_OFFER
    }),
    getAddFileUrl(messageId) {
      const host = APIService.host;
      return `${host}/conversation-messages/${messageId}/file`;
    },
    async setFileUrl(messageId, fileId) {
      return this.uppyInstance.setFileState(fileId, {
        xhrUpload: { endpoint: this.getAddFileUrl(messageId) }
      });
    },
    getErrors(errors) {
      return this.showValidationErrors ? errors : [];
    },
    async onMessageTemplateChange({ body, attachments }) {
      this.body = this.isEmail ? body : convertHtmlToText(body);
      const messageTemplateFiles = await getFilesFromUrls(attachments);
      forEach(messageTemplateFiles, file => {
        this.$refs.fileUpload.setFile({ ...file, type: file.mimeType });
      });
    },
    selectFile({ uppyInstance, file }) {
      this.uppyInstance = uppyInstance;
      this.files.push(file);
    },
    unselectFile({ uppyInstance, file }) {
      this.removeFile(file);
      uppyInstance.reset();
    },
    removeFile(fileToBeRemoved) {
      remove(this.files, file => file.id === fileToBeRemoved.id);
    },
    async getAttachments() {
      this.attachments = [];
      await Promise.all(
        map(this.files, async file => {
          const data = await convertBlobFileToBase64(file.data);
          this.attachments.push({ data, fileName: file.name });
        })
      );
    },
    async addMessage() {
      this.showValidationErrors = true;
      await this.$refs.message.validate();
      await this.getAttachments();
      if (size(this.body)) {
        this.isLoading = true;
        await this.addConversationMessage({
          body: this.isEmail ? getConvertedEmailBody(this.body) : this.body,
          attachments: this.attachments,
          conversation: {
            id: this.currentConversationId
          }
        });
        this.clearNewMessageData();
        this.fetchConversation(this.currentConversationId);
        this.isLoading = false;
        this.showValidationErrors = false;
      }
    },
    async closeConversation() {
      this.isEditing = true;
      await this.editConversation({
        id: this.currentConversationId,
        data: {
          isOpen: false
        }
      });
      this.isEditing = false;
      this.$router.push("/conversations");
    },
    async uploadFiles(messageId) {
      forEach(this.files, ({ id }) => this.setFileUrl(messageId, id));
      return this.uppyInstance && this.uppyInstance.upload(messageId);
    },
    clearNewMessageData() {
      this.body = null;
      this.selectedTemplate = null;
      this.files = [];
    },
    async fetchEntity() {
      const { relatedToType, relatedToId } = this.currentConversation;
      switch (relatedToType) {
        case "booking":
          await this.fetchCurrentBooking(relatedToId);
          break;
        case "offer":
          await this.fetchCurrentOffer(relatedToId);
          break;
        case "reference":
          await this.fetchCurrentReference(relatedToId);
      }
    },
    async adjustMessageTemplates() {
      const { relatedToType, relatedToId } = this.currentConversation;
      if (!relatedToType) {
        return;
      }
      let bookingIds;
      switch (relatedToType) {
        case "booking":
          bookingIds = [relatedToId];
          break;
        case "offer":
          bookingIds = [this.currentOffer.application.booking.id];
          break;
        case "reference":
          bookingIds = map(
            this.currentReference.applications,
            ({ booking }) => booking.id
          );
      }
      this.fetchMessageTemplates(bookingIds);
    },
    adjustVariablesTextContent,
    size
  }
};
</script>

<style lang="scss">
.conversation-details {
  .new-message {
    .file-actions {
      position: initial !important;
    }

    @media only screen and (max-width: $mobile-breakpoint - 1) {
      margin-bottom: 50px;
    }
  }
  .close-conversation-action {
    svg path {
      fill: $primary-darker;
    }
  }
  .related-to-link {
    margin: auto;
    margin-bottom: 20px;
  }
}
</style>
